// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/no-misused-promises */
// Added to allow async functions passed to apple pay session callbacks

import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

import { PartialAddress } from '@src/quince-site-common/src/services/addressService/addressType'
import {
  CheckoutExpressUpdateDetail,
  EnrichedCheckout,
  ShippingMethods,
} from '@src/quince-site-common/src/services/checkout-service-full/checkout-service-full.types'
import { handleApplePayPaymentFailedTracking } from '@src/quince-site-common/src/trackers/helpers/handleApplePayTracking'

import {
  createApplePaySession,
  getPartialAddress,
  handlePaymentAuthorization,
  initializeApplePay,
  makeApplePayBraintreeShippingMethods,
  makeBraintreeLineItems,
  makeCheckoutExpressUpdateDetail,
  OnPaymentAuthorizedEvent,
  OnShippingContactSelectedEvent,
  OnShippingMethodSelectedEvent,
  UpdateExpressCompleteDetails,
} from './apple-pay-button.helpers'

import type { ApplePay, ApplePayStatusCodes } from 'braintree-web'

const BRAINTREE_LINE_ITEM_DEFAULT_TYPE_FINAL = 'final'

interface UseApplePayBraintreeProps {
  onError?: (error?: unknown) => void
  getCheckoutDetails: (isPreorder?: boolean) => Promise<EnrichedCheckout | null>
  updateShippingAddress: (address: PartialAddress) => Promise<CheckoutExpressUpdateDetail>
  updateShippingOption: (shippingOptionId: ShippingMethods) => Promise<CheckoutExpressUpdateDetail>
  validateApplePayClick?: () => boolean
  productPrice?: number
  checkoutExpressUpdateDetail: CheckoutExpressUpdateDetail | null
  updateExpressCompleteDetails: UpdateExpressCompleteDetails
  capture: () => Promise<void>
  skipEmail?: boolean
  getZeroAmountCheckoutUrl?: () => string | null
  showZeroAmountCheckoutModal?: () => void
}

export const useApplePayBraintree = ({
  onError,
  getCheckoutDetails,
  validateApplePayClick,
  productPrice,
  updateShippingAddress,
  updateShippingOption,
  checkoutExpressUpdateDetail,
  updateExpressCompleteDetails,
  capture,
  skipEmail = false,
  getZeroAmountCheckoutUrl,
  showZeroAmountCheckoutModal,
}: UseApplePayBraintreeProps) => {
  const { t } = useTranslation('common')
  const [applePayInstance, setApplePayInstance] = useState<ApplePay | null>(null)
  const [isSupported, setIsSupported] = useState(false)
  const [isApplePayClickInProgress, setIsApplePayClickInProgress] = useState(false)
  const checkoutExpressUpdateDetailRef = useRef<CheckoutExpressUpdateDetail | null>(checkoutExpressUpdateDetail)

  useEffect(() => {
    checkoutExpressUpdateDetailRef.current = checkoutExpressUpdateDetail
  }, [checkoutExpressUpdateDetail])

  useEffect(() => {
    const setupApplePay = async () => {
      try {
        const { applePayInstance, isSupported } = await initializeApplePay(onError)
        setIsSupported(isSupported)
        setApplePayInstance(applePayInstance)
      } catch (err) {
        console.error('Error creating Braintree ApplePay instance:', err)
      }
    }

    void setupApplePay()
  }, [onError])

  const handleApplePayClick = () => {
    if (!applePayInstance) return
    if (isApplePayClickInProgress) return

    try {
      setIsApplePayClickInProgress(true)
      if (!window.ApplePaySession) {
        onError?.(new Error('Apple Pay is not supported'))
        setIsApplePayClickInProgress(false)
        return
      }

      // Synchronous validations to check if payment is possible
      const isValid = validateApplePayClick?.()
      if (!isValid) {
        setIsApplePayClickInProgress(false)
        return
      }

      const checkoutDetailsPromise = getCheckoutDetails()
      void checkoutDetailsPromise.catch(() => {
        // The Apple Pay session handlers consume this promise later.
      })
      const initialDetail = makeCheckoutExpressUpdateDetail(undefined, undefined, productPrice)
      const session = createApplePaySession(applePayInstance, initialDetail, true, t, skipEmail)

      if (!session) {
        onError?.(new Error('Unable to create Apple Pay session'))
        setIsApplePayClickInProgress(false)
        return
      }

      session.onvalidatemerchant = (event: { validationURL: string }) => {
        applePayInstance.performValidation(
          {
            validationURL: event.validationURL,
            displayName: 'Quince',
          },
          (err, merchantSession) => {
            if (err) {
              console.error('Apple Pay validation failed:', err)
              setIsApplePayClickInProgress(false)
              session.abort()
              onError?.(err)
              return
            }
            session.completeMerchantValidation(merchantSession)
          },
        )
      }

      session.onshippingcontactselected = async (event: OnShippingContactSelectedEvent) => {
        try {
          const checkoutDetails = await checkoutDetailsPromise

          if (!checkoutDetails) {
            setIsApplePayClickInProgress(false)
            session.abort()
            // `getCheckoutDetails` can return null for two distinct reasons:
            // 1. Zero-amount coupon flow: `useExpressPay` stores a checkout URL that
            //    `getZeroAmountCheckoutUrl` exposes, so we open the fallback modal.
            // 2. Mini checkout creation failed: no fallback URL is available, so we
            //    surface the existing checkout creation error.
            if (!getZeroAmountCheckoutUrl?.()) {
              onError?.(new Error('Failed to create checkout'))
            } else {
              showZeroAmountCheckoutModal?.()
            }
            return
          }
          const { partialAddress, newTotal } = await getPartialAddress(event, updateShippingAddress)

          session.completeShippingContactSelection(
            window.ApplePaySession?.STATUS_SUCCESS as ApplePayStatusCodes,
            makeApplePayBraintreeShippingMethods(partialAddress, t),
            newTotal,
            makeBraintreeLineItems(partialAddress, BRAINTREE_LINE_ITEM_DEFAULT_TYPE_FINAL, true),
          )
        } catch (err) {
          console.error('Error updating shipping contact:', err)
          setIsApplePayClickInProgress(false)
          session.abort()
          onError?.(err)
        }
      }

      session.onshippingmethodselected = async (event: OnShippingMethodSelectedEvent) => {
        try {
          const updatedShippingOptionData = await updateShippingOption(
            event.shippingMethod.identifier as ShippingMethods,
          )
          const newTotal = {
            label: 'Quince',
            amount: updatedShippingOptionData.amountToCollect.toFixed(2),
          }
          session.completeShippingMethodSelection(
            window.ApplePaySession?.STATUS_SUCCESS as ApplePayStatusCodes,
            newTotal,
            makeBraintreeLineItems(updatedShippingOptionData, BRAINTREE_LINE_ITEM_DEFAULT_TYPE_FINAL, true),
          )
        } catch (err) {
          console.error('Error updating shipping method:', err)
          setIsApplePayClickInProgress(false)
          session.abort()
          onError?.(err)
        }
      }

      session.onpaymentauthorized = async (event: OnPaymentAuthorizedEvent) => {
        try {
          const latestCheckoutExpressUpdateDetail =
            checkoutExpressUpdateDetailRef.current ||
            checkoutExpressUpdateDetail ||
            makeCheckoutExpressUpdateDetail(await checkoutDetailsPromise, undefined, productPrice)

          await handlePaymentAuthorization(
            event,
            session,
            applePayInstance,
            latestCheckoutExpressUpdateDetail,
            updateExpressCompleteDetails,
            capture,
            skipEmail,
          )
          setIsApplePayClickInProgress(false)
        } catch (err) {
          console.error('Apple Pay payment authorization failed:', err)
          handleApplePayPaymentFailedTracking(err)
          setIsApplePayClickInProgress(false)
          onError?.(err)
        }
      }

      session.oncancel = () => {
        setIsApplePayClickInProgress(false)
      }

      try {
        session.begin()
      } catch (beginError) {
        console.error('Error calling session.begin() on apple pay click:', beginError)
        setIsApplePayClickInProgress(false)
        onError?.(beginError)
      }
    } catch (error) {
      console.error('Apple Pay payment failed:', error)
      setIsApplePayClickInProgress(false)
      onError?.(error)
    }
  }

  return { applePayInstance, isApplePayClickInProgress, isSupported, handleApplePayClick }
}
